/**
 * Cómo se parten las conversaciones.
 *
 * En el dashboard del negocio: una barra y etiquetas con clic directo.
 * En la portada del admin (spec 034): las mismas etiquetas, y el clic abre
 * el desglose por negocio.
 *
 *   conversaciones = en curso + terminadas + a medias
 *   terminadas     = cita + anularon + persona + consultaron + espera + no encajó
 */
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'

import { filasDeTramo, rutaContador, type ClaveContador } from '../lib/desglose-portada'
import { cn } from '../lib/cn'

export { rutaContador }

export interface ContadoresVista {
    total: number
    enCurso: number
    finalizadas: number
    cerradasBien: number
    canceladas: number
    incidencias: number
    escaladas: number
    informativas: number
    enEspera: number
    sinEncaje: number
    colgadas: number
}

export interface NegocioDeContadores {
    accountId: number
    name: string
    contadores: ContadoresVista
}

interface Tramo {
    clave: ClaveContador
    titulo: string
    color: string
}

const DONDE: Tramo[] = [
    { clave: 'enCurso', titulo: 'Siguen hablando', color: 'bg-sky-400' },
    { clave: 'finalizadas', titulo: 'Ya terminaron', color: 'bg-emerald-400' },
    { clave: 'colgadas', titulo: 'A medias', color: 'bg-rose-400' },
]

const ACABARON: Tramo[] = [
    { clave: 'cerradasBien', titulo: 'Acabaron en cita', color: 'bg-emerald-400' },
    { clave: 'canceladas', titulo: 'Anularon', color: 'bg-amber-400' },
    { clave: 'escaladas', titulo: 'Pasaron a una persona', color: 'bg-violet-400' },
    { clave: 'informativas', titulo: 'Consultaron', color: 'bg-cyan-400' },
    { clave: 'enEspera', titulo: 'En espera', color: 'bg-orange-400' },
    { clave: 'sinEncaje', titulo: 'No encajó', color: 'bg-stone-400' },
]

function Barra({
    tramos,
    datos,
    href,
}: {
    tramos: Tramo[]
    datos: ContadoresVista
    href: (clave: ClaveContador) => string
}) {
    const suma = tramos.reduce((n, t) => n + datos[t.clave], 0)

    return (
        <div className="space-y-3">
            <div
                data-testid="barra-contadores"
                className="flex h-3 overflow-hidden rounded-full bg-neutral-800"
            >
                {tramos.map((t) => {
                    const v = datos[t.clave]
                    if (v <= 0 || suma === 0) return null
                    return (
                        <div
                            key={t.clave}
                            className={cn(t.color)}
                            style={{ width: `${(v / suma) * 100}%` }}
                            title={`${t.titulo}: ${v}`}
                        />
                    )
                })}
            </div>
            <ul className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
                {tramos.map((t) => {
                    const v = datos[t.clave]
                    return (
                        <li key={t.clave}>
                            <Link
                                data-testid={`contador-${t.clave}`}
                                data-valor={String(v)}
                                to={href(t.clave)}
                                className="inline-flex min-h-11 items-center gap-2 hover:text-neutral-200 md:min-h-0"
                            >
                                <CuerpoEtiqueta color={t.color} valor={v} titulo={t.titulo} />
                            </Link>
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}

function CuerpoEtiqueta({ color, valor, titulo }: { color: string; valor: number; titulo: string }) {
    return (
        <>
            <span className={cn('h-2 w-2 shrink-0 rounded-full', color)} />
            <span className="tabular-nums font-medium text-neutral-100">{valor}</span>
            <span className="text-neutral-400">{titulo}</span>
        </>
    )
}

function EtiquetasConMenu({
    tramos,
    datos,
    negocios,
    periodo,
    incluirTests,
    total,
}: {
    tramos: Tramo[]
    datos: ContadoresVista
    negocios: readonly NegocioDeContadores[]
    periodo: string
    incluirTests: boolean
    total?: boolean
}) {
    const [abierta, setAbierta] = useState<ClaveContador | null>(null)
    const caja = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (abierta === null) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setAbierta(null)
        }
        const onPtr = (e: PointerEvent) => {
            if (caja.current && !caja.current.contains(e.target as Node)) setAbierta(null)
        }
        document.addEventListener('keydown', onKey)
        document.addEventListener('pointerdown', onPtr)
        return () => {
            document.removeEventListener('keydown', onKey)
            document.removeEventListener('pointerdown', onPtr)
        }
    }, [abierta])

    const items: Tramo[] = total
        ? [{ clave: 'total', titulo: 'en total', color: 'bg-neutral-400' }, ...tramos]
        : tramos

    return (
        <div ref={caja} data-testid="etiquetas-contadores" className="flex flex-wrap gap-2">
            {items.map((t) => {
                const v = datos[t.clave]
                const abiertaEsta = abierta === t.clave
                const filas = filasDeTramo(negocios, t.clave, periodo, incluirTests)
                return (
                    <div key={t.clave} className="relative">
                        <button
                            type="button"
                            data-testid={`contador-${t.clave}`}
                            data-valor={String(v)}
                            aria-expanded={abiertaEsta}
                            aria-haspopup="dialog"
                            onClick={() => setAbierta(abiertaEsta ? null : t.clave)}
                            className={
                                'inline-flex min-h-11 items-center gap-2 rounded-full border border-neutral-800 bg-neutral-900/60 px-3 py-1.5 text-sm transition hover:border-neutral-600 hover:bg-neutral-900 md:min-h-0 ' +
                                (abiertaEsta ? 'border-neutral-500 bg-neutral-900' : '')
                            }
                        >
                            <CuerpoEtiqueta color={t.color} valor={v} titulo={t.titulo} />
                        </button>
                        {abiertaEsta && (
                            <div
                                role="dialog"
                                aria-label={`${t.titulo} por negocio`}
                                data-testid={`menu-tramo-${t.clave}`}
                                className="absolute left-0 z-30 mt-2 w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-neutral-700 bg-neutral-950 shadow-xl shadow-black/40"
                            >
                                <p className="border-b border-neutral-800 px-3 py-2 text-[11px] uppercase tracking-wide text-neutral-500">
                                    {t.titulo} por negocio
                                </p>
                                {filas.length === 0 ? (
                                    <p className="px-3 py-3 text-sm text-neutral-500">Ningún negocio en este recuento.</p>
                                ) : (
                                    <ul className="max-h-72 overflow-y-auto py-1">
                                        {filas.map((f) => (
                                            <li key={f.accountId}>
                                                <Link
                                                    to={f.destino}
                                                    onClick={() => setAbierta(null)}
                                                    className="flex min-h-11 items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-neutral-900 md:min-h-0"
                                                >
                                                    <span className="truncate text-neutral-100">{f.name}</span>
                                                    <span
                                                        className={
                                                            'shrink-0 tabular-nums ' +
                                                            (f.valor > 0 ? 'font-medium text-neutral-100' : 'text-neutral-600')
                                                        }
                                                    >
                                                        {f.valor}
                                                    </span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

export function Contadores({
    datos,
    accountId,
    periodo,
    soloLectura = false,
    incluirTests = false,
    porNegocio,
}: {
    datos: ContadoresVista
    accountId: number
    periodo: string
    soloLectura?: boolean
    incluirTests?: boolean
    /** Portada admin: el clic abre el menú por negocio (034). */
    porNegocio?: readonly NegocioDeContadores[]
}) {
    const href = (clave: ClaveContador) => rutaContador(clave, accountId, periodo, incluirTests)
    const conMenu = porNegocio !== undefined

    return (
        <div className="space-y-6 rounded-2xl border border-neutral-800 bg-neutral-900/30 p-5">
            <section className="space-y-3">
                <header className="flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="text-sm font-medium text-neutral-200">Qué pasó con quien escribió</h3>
                    {!conMenu && (
                        <Link
                            data-testid="contador-total"
                            data-valor={String(datos.total)}
                            to={soloLectura ? '#' : href('total')}
                            className={
                                soloLectura
                                    ? 'pointer-events-none text-sm tabular-nums text-neutral-400'
                                    : 'text-sm tabular-nums text-neutral-400 hover:text-neutral-200'
                            }
                        >
                            {datos.total} en total
                        </Link>
                    )}
                </header>
                {conMenu ? (
                    <EtiquetasConMenu
                        tramos={DONDE}
                        datos={datos}
                        negocios={porNegocio}
                        periodo={periodo}
                        incluirTests={incluirTests}
                        total
                    />
                ) : (
                    <Barra tramos={DONDE} datos={datos} href={href} />
                )}
            </section>

            {datos.finalizadas > 0 && (
                <section className="space-y-3">
                    <h3 className="text-sm font-medium text-neutral-200">De las que ya terminaron</h3>
                    {conMenu ? (
                        <EtiquetasConMenu
                            tramos={ACABARON}
                            datos={datos}
                            negocios={porNegocio}
                            periodo={periodo}
                            incluirTests={incluirTests}
                        />
                    ) : (
                        <Barra tramos={ACABARON} datos={datos} href={href} />
                    )}
                </section>
            )}

            {datos.finalizadas === 0 && (
                <div className="hidden">
                    {ACABARON.map((t) => (
                        <span key={t.clave} data-testid={`contador-${t.clave}`} data-valor={String(datos[t.clave])} />
                    ))}
                </div>
            )}

            <div
                data-testid="contador-incidencias"
                data-valor={String(datos.incidencias)}
                className={datos.incidencias > 0 ? 'rounded-xl border border-rose-900/50 bg-rose-950/20 px-4 py-3' : 'hidden'}
            >
                {datos.incidencias > 0 && (
                    <p className="text-sm text-rose-300">
                        {datos.incidencias === 1
                            ? 'Hubo 1 choque de hueco: dos personas quisieron la misma hora.'
                            : `Hubo ${datos.incidencias} choques de hueco: dos personas quisieron la misma hora.`}
                    </p>
                )}
            </div>
        </div>
    )
}
